import { useState } from "react";
import type { Poll } from "./types";

interface PollPanelProps {
  isHost: boolean;
  poll: Poll | null;
  myVote: number | null;
  onVote: (optionIndex: number) => void;
  onCreate: (question: string, options: string[]) => void;
  onClose: () => void;
}

export default function PollPanel({
  isHost,
  poll,
  myVote,
  onVote,
  onCreate,
  onClose,
}: PollPanelProps) {
  const [question, setQuestion] = useState("");
  const [optionsText, setOptionsText] = useState("");

  const totalVotes = poll ? poll.votes.reduce((s, v) => s + v, 0) : 0;

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const opts = optionsText
      .split("\n")
      .map((o) => o.trim())
      .filter(Boolean);
    if (!question.trim() || opts.length < 2) return;
    onCreate(question.trim(), opts.slice(0, 5));
    setQuestion("");
    setOptionsText("");
  };

  return (
    <div className="w-full md:w-96 h-full bg-background-900 border-l border-background-700/50 flex flex-col shrink-0">
      <div className="h-12 px-4 flex items-center justify-between border-b border-background-700/50 shrink-0">
        <h2 className="text-sm font-bold text-background-50">Canlı Anket</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-background-300 hover:bg-background-700 transition-colors"
          aria-label="Kapat"
        >
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {!poll ? (
          isHost ? (
            <form onSubmit={handleCreate} className="space-y-3">
              <p className="text-xs text-background-400 leading-relaxed">
                Sınıfa hızlı bir soru sor, öğrencilerin cevaplarını canlı olarak gör.
              </p>
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Soru (örn: Bu konu anlaşıldı mı?)"
                className="w-full h-10 px-3 rounded-lg bg-background-800 border border-background-700 text-sm text-background-100 placeholder:text-background-500 focus:outline-none focus:border-primary-500"
              />
              <textarea
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={"Seçenekler (her satıra bir tane)\nEvet\nHayır\nEmin değilim"}
                rows={4}
                className="w-full px-3 py-2 rounded-lg bg-background-800 border border-background-700 text-sm text-background-100 placeholder:text-background-500 focus:outline-none focus:border-primary-500 resize-none"
              />
              <button
                type="submit"
                className="w-full h-10 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Anketi Başlat
              </button>
            </form>
          ) : (
            <p className="text-xs text-background-400 text-center py-8">
              Öğretmen henüz bir anket başlatmadı.
            </p>
          )
        ) : (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-background-100">{poll.question}</h3>
            <div className="space-y-2">
              {poll.options.map((opt, i) => {
                const count = poll.votes[i] ?? 0;
                const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                const chosen = myVote === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onVote(i)}
                    disabled={myVote !== null}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors relative overflow-hidden disabled:cursor-not-allowed ${
                      chosen
                        ? "border-primary-500 bg-primary-500/10 text-background-50"
                        : "border-background-700 bg-background-800 text-background-200 hover:border-background-600"
                    }`}
                  >
                    {isHost && (
                      <span
                        className="absolute inset-y-0 left-0 bg-primary-500/20"
                        style={{ width: `${pct}%` }}
                        aria-hidden="true"
                      ></span>
                    )}
                    <span className="relative flex items-center justify-between gap-2">
                      <span>{opt}</span>
                      {isHost && (
                        <span className="text-xs font-bold text-primary-400 whitespace-nowrap">
                          {count} ({pct}%)
                        </span>
                      )}
                      {chosen && <i className="ri-check-line" aria-hidden="true"></i>}
                    </span>
                  </button>
                );
              })}
            </div>
            {!isHost && myVote !== null && (
              <p className="text-xs text-accent-400 text-center">Oyunuz alındı. Teşekkürler!</p>
            )}
            {isHost && (
              <p className="text-xs text-background-400 text-center">{totalVotes} oy toplandı</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}